import React from 'react';
import { cn } from '../../lib/cn';

/**
 * Page furniture shared by every settings page (spec 15 §2.5–2.7), restyled to
 * `SETTINGS-STYLE-GUIDE.md` (2 Oct).
 *
 * Three changes carry most of the new look:
 * 1. **The card head is white**, not a `#F4F6FB` fill — the tint greyed down every
 *    page, and the 3px accent bar already carries the meaning (§4).
 * 2. **Row dividers are solid hairlines**, not dotted. Dotted reads as a sketch (§5).
 * 3. **The page sub is 14px**, up from 13 — it was set smaller than the row
 *    descriptions below it, which is backwards (§2).
 *
 * The literal hex values below are settings-scoped and live only here.
 */

/** Solid hairline between rows and under a card head (§5). */
const HAIRLINE = '#edf0f6';

/** Accent bar colour carries meaning and is set per card on the boards (§2.6). */
export type SettingsAccent = 'indigo' | 'red' | 'amber' | 'green';

const ACCENT: Record<SettingsAccent, string> = {
  indigo: 'var(--brand-indigo)',
  red: 'var(--alert-red)',
  amber: '#D9A22B',
  green: 'var(--money-green)',
};

/** Eyebrow · H1 (two words, the second indigo) · one-line sub (§2.5). */
export function SettingsPageHeader({
  eyebrow,
  titleLead,
  titleAccent,
  sub,
}: {
  eyebrow: string;
  titleLead: string;
  titleAccent?: string;
  sub: React.ReactNode;
}) {
  return (
    <header className="mb-7">
      <div className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-[var(--text-muted)]">{eyebrow}</div>
      <h1 className="m-0 mt-2 text-[30px] font-bold leading-[1.1] tracking-[-0.03em] text-[var(--text-primary)]">
        {titleLead}
        {/* §7: the accent word takes the reference screens' playful multi-hue sweep —
            red → amber → green → indigo → violet, anchored to the DS semantic colours. */}
        {titleAccent ? (
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage:
                'linear-gradient(92deg, var(--alert-red) 0%, #d9a22b 26%, var(--money-green) 52%, var(--brand-indigo) 78%, var(--violet) 100%)',
            }}
          >
            {' '}
            {titleAccent}
          </span>
        ) : null}
      </h1>
      <p className="m-0 mt-2.5 max-w-[780px] text-[14px] leading-[1.65] text-[var(--text-muted)]">{sub}</p>
    </header>
  );
}

/** Cards stack with a 26px gap (§2.5). */
export function SettingsCards({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-[26px]">{children}</div>;
}

/** A `sect` — 3px accent bar, tinted head, body, optional tinted footer notes (§2.6). */
export function SettingsCard({
  n,
  id,
  title,
  sub,
  accent = 'indigo',
  headRight,
  children,
  className,
}: {
  /** The `01`, `02`… index shown in the head. */
  n?: string;
  /** A deep-link anchor, so a link from another page lands on THIS card rather
   * than the top of a long one (amendment 20 §2.4). */
  id?: string;
  title: string;
  sub?: React.ReactNode;
  accent?: SettingsAccent;
  headRight?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      id={id}
      className={cn(
        'overflow-hidden rounded-[16px] border border-[var(--border-default)] bg-[var(--surface-card)]',
        className,
      )}
      // A little breathing room above an anchored card, so it does not land
      // flush against the top of the scroll container.
      style={{ boxShadow: 'var(--sh-1)', scrollMarginTop: 24 }}
    >
      {/* §4: white head, 3px accent bar, hairline under it so the head still reads
          as a header without a fill. */}
      <div
        className="flex items-start gap-3.5 bg-[var(--surface-card)] px-6 py-[18px]"
        style={{ borderLeft: `3px solid ${ACCENT[accent]}`, borderBottom: `1px solid ${HAIRLINE}` }}
      >
        {n ? (
          <span className="mt-[3px] inline-flex shrink-0 items-center rounded-[6px] bg-[#f1f4fa] px-1.5 py-[3px] font-[var(--font-mono)] text-[10.5px] font-bold text-[var(--text-muted)]">
            {n}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <h2 className="m-0 text-[16px] font-bold tracking-[-0.01em] text-[var(--text-primary)]">{title}</h2>
          {sub ? (
            <p className="m-0 mt-1 max-w-[660px] text-[12.5px] leading-[1.6] text-[var(--text-muted)]">{sub}</p>
          ) : null}
        </div>
        {headRight ? <div className="shrink-0">{headRight}</div> : null}
      </div>
      {children}
    </section>
  );
}

/**
 * A `srow` — label + optional desc on the left, the control on the right.
 * 1px solid hairline between rows, none after the last (§2.7, style guide §5).
 */
export function SettingsRow({
  label,
  desc,
  control,
  children,
  last,
  muted,
  className,
}: {
  label: React.ReactNode;
  desc?: React.ReactNode;
  /** The control on the right. */
  control?: React.ReactNode;
  /** Full-width content below the label, for rows that are not label+control. */
  children?: React.ReactNode;
  last?: boolean;
  muted?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn('px-6 py-[18px]', className)}
      style={last ? undefined : { borderBottom: `1px solid ${HAIRLINE}` }}
    >
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0 flex-1">
          <div
            className={cn(
              'text-[14.5px] font-semibold',
              muted ? 'text-[var(--text-muted)]' : 'text-[var(--text-primary)]',
            )}
          >
            {label}
          </div>
          {desc ? (
            <div className="mt-1 max-w-[640px] text-[13px] leading-[1.6] text-[var(--text-muted)]">{desc}</div>
          ) : null}
        </div>
        {control ? <div className="shrink-0">{control}</div> : null}
      </div>
      {children}
    </div>
  );
}

/**
 * A tinted footer note. These are content, not chrome — several of them are the
 * only place a rule is stated, so they ship (§0).
 */
export function SettingsNote({
  tone = 'plain',
  children,
}: {
  tone?: 'plain' | 'indigo' | 'amber' | 'red' | 'green';
  children: React.ReactNode;
}) {
  const tones: Record<string, { bg: string; fg: string }> = {
    plain: { bg: 'var(--surface-sunken)', fg: 'var(--text-muted)' },
    indigo: { bg: 'var(--brand-indigo-tint)', fg: 'var(--text-secondary)' },
    amber: { bg: '#FBF3E4', fg: 'var(--warn-amber)' },
    red: { bg: '#FBEDEC', fg: 'var(--alert-red)' },
    green: { bg: '#EEF6F2', fg: '#1B5F45' },
  };
  const t = tones[tone] ?? tones.plain;
  return (
    <div
      className="px-6 py-[15px] text-[12.5px] leading-[1.65]"
      style={{ background: t?.bg, color: t?.fg }}
    >
      {children}
    </div>
  );
}

/**
 * The page footer — a thin muted line, not a card and with no border (§2.5).
 * Derived counts on the left, which location is being edited on the right.
 */
export function SettingsFooter({ left, location }: { left?: React.ReactNode; location: string }) {
  return (
    <div className="mt-6 flex flex-wrap items-start justify-between gap-4 text-[11.5px] leading-[1.6] text-[var(--text-muted)]">
      <div>{left}</div>
      <div>
        Editing <span className="font-semibold text-[var(--text-secondary)]">{location}</span>.
      </div>
    </div>
  );
}

/** A row of rows with no card chrome, used inside a card body. */
export function SettingsRows({ children }: { children: React.ReactNode }) {
  return <div>{children}</div>;
}
